import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Language, Release } from 'app/modules/user/releases/releases.types';
import { combineLatestWith, debounceTime, map, take, tap } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Country, ObjectLiteral } from 'app/shared/shared.types';
import { countryFlags } from 'assets/data/countries';
import { TranslocoService } from '@ngneat/transloco';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { ReleaseSummaryModal } from 'app/shared/components/release-summary-modal/release-summary-modal.component';

@Injectable({
    providedIn: 'root'
})
export class UtilService {
    private _languages: BehaviorSubject<Language[] | null> = new BehaviorSubject(null);
    private _countries: BehaviorSubject<Country[] | null> = new BehaviorSubject(null);
    private flags: ObjectLiteral;

    constructor(
        private _httpClient: HttpClient,
        private translocoService: TranslocoService,
        private _sanitizer: DomSanitizer,
        private _matDialog: MatDialog,
    ) {
    }

    get languages$(): Observable<Language[]> {
        return this._languages.asObservable();
    }

    get countries$(): Observable<Country[]> {
        return this._countries.asObservable();
    }

    getDateString(d: Date): string {
        return d.toISOString().substring(0, 10);
    }

    getLanguages(): Observable<Language[]> {
        let langs: Language[];
        this._languages.pipe(take(1)).subscribe(l => langs = l);
        if (langs) {
            return of(langs);
        }

        return this._httpClient.get<Language[]>('api/common/languages').pipe(
            tap((languages) => {
                this._languages.next(languages);
            })
        );
    }

    getCountries(): Observable<Country[]> {
        let countries: Country[];
        this._countries.pipe(take(1)).subscribe(c => countries = c);
        if (countries) {
            return of(countries);
        }

        let flags$ = this._httpClient.get('./assets/images/flags/flags.json');
        let countries$ = this._httpClient.get<Country[]>('api/common/countries');

        return countries$.pipe(
            combineLatestWith(flags$),
            tap(([countries, flags]) => {
                var i = countries.length
                while (i-- > 0) {
                    let country = countries[i];
                    let flag = flags[country.alpha2];
                    if (!flag) {
                        //Remove country if no flag is available
                        countries.splice(i, 1);
                        console.warn('Couldn\'t find flag for ' + country.alpha2);
                    } else {
                        country.flagImage = this._sanitizer.bypassSecurityTrustResourceUrl(flag);
                    }


                    let flagData = countryFlags.find(flag => flag.iso === country.alpha2);
                    if (!flagData) {
                        country.flagImagePos = null;
                        //console.warn('Couldn\'t find flag pos for ' + country.alpha2);
                    } else {
                        country.flagImagePos = flagData.flagImagePos;
                    }
                }

                this._countries.next(countries);

                //Load names and keep subscription active in case of language changes
                //TODO only works on first language switch
                this.translocoService.events$.pipe(debounceTime(300)).subscribe(events => {
                    countries.forEach(country => {
                        country.name = this.translocoService.translate(country.alpha2, {}, 'cc');
                    });
                    countries = countries.sort((a, b) => a.name.localeCompare(b.name));
                    this._countries.next(countries);
                });
            }),
            map(([countries, flags]) => {
                return countries;
            })
        );
    }

    getFlagPositionForCountry(country: Country): string {
        if (!country) {
            return
        }
        if (country.flagImagePos) {
            return country.flagImagePos;
        }

        if (country.flagImagePos === null) {
            return;
        }

        let flagData = countryFlags.find(flag => flag.iso === country.alpha2);
        if (!flagData) {
            console.warn('Couldn\'t find flag data for ' + country.alpha2);
            return;
        }

        country.flagImagePos = flagData.flagImagePos;
        return country.flagImagePos;
    }

    getCountryFromAlpha2(alpha2: string): Country {
        if (!alpha2) {
            return null;
        }
        let country = this._countries.getValue().find(country => country.alpha2.toLowerCase() === alpha2.toLowerCase());
        if (!country) {
            return null;
        }
        return country;
    }

    getCountryNameFromAlpha2(alpha2: string): string {
        if (!alpha2) {
            return "Unknown Location";
        }
        let country = this._countries.getValue().find(country => country.alpha2.toLowerCase() === alpha2.toLowerCase());
        if (!country) {
            return alpha2;
        }
        return this.getCountryName(country);
    }

    getCountryName(country: Country) {
        if (country.name && !country.name.startsWith("cc.")) {
            return country.name;
        }
        country.name = this.translocoService.translate('cc.' + country.alpha2);
        return country.name;
    }

    /**
     *
     * @param code Either a 12 character UPC code or a 13 character EAN code
     */
    isValidBarcode(code: string): boolean {
        if (!code.match(/^(\d{12,13})$/)) {
            return false;
        }

        const paddedCode = code.padStart(14, '0');

        let result = 0;
        for (let i = 0; i < paddedCode.length - 1; i += 1) {
            result += parseInt(paddedCode.charAt(i), 10) * ((i % 2 === 0) ? 3 : 1);
        }

        return ((10 - (result % 10)) % 10) === parseInt(paddedCode.charAt(13), 10);
    }

    isValidISRC(code: string): boolean {
        if (!code.match(/^([\da-zA-Z]{5}\d{7})$/)) {
            return false;
        }

        return true;
    }

    openReleaseModalWithRelease(release: Release): void {
        this._matDialog.open(ReleaseSummaryModal, {
            panelClass: 'release-summary-modal',
            autoFocus: false,
            data: {
                release: release
            }
        });
    }

    openReleaseModalWithUuid(releaseUuid: string): void {
        this._matDialog.open(ReleaseSummaryModal, {
            panelClass: 'release-summary-modal',
            autoFocus: false,
            data: {
                releaseUuid: releaseUuid
            }
        });
    }
}


